import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

/**
 * One-time gate shown to any authenticated user without `terms_accepted_at`.
 * Most commonly fires for Google-OAuth signups that bypass the email/password
 * form (where Terms is collected inline). Non-dismissible — blocks the app
 * until the user accepts. Calls POST /auth/accept-terms.
 */
const TermsGateModal = () => {
  const { user, loading, refresh } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [marketing, setMarketing] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const needed = !loading && user && user.needs_terms === true;
  if (!needed) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) {
      setErr("Please accept the Terms & Privacy Policy to continue.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await api.post(
        "/auth/accept-terms",
        { terms_accepted: true, marketing_opt_in: marketing },
        { withCredentials: true }
      );
      await refresh();
      toast.success("Welcome to Spike Sports.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Could not save your preferences.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="terms-gate-modal"
    >
      <div className="w-full max-w-md bg-spike-card border border-spike-blue/30 rounded-lg shadow-2xl overflow-hidden">
        <div className="px-6 pt-6 pb-4 border-b border-white/10">
          <div className="flex items-center gap-2 text-spike-blue-light mb-2">
            <ShieldCheck className="w-5 h-5" />
            <span className="font-orbitron text-[11px] uppercase tracking-[0.25em] font-bold">
              One more step
            </span>
          </div>
          <h2 className="text-xl font-orbitron font-bold text-spike-text uppercase tracking-wide">
            Accept Terms to continue
          </h2>
          <p className="mt-2 text-sm text-spike-text-muted font-exo">
            Before you book a lane or sign your waiver, please review and accept our Terms &amp; Privacy Policy.
          </p>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <label className="flex items-start gap-2.5 cursor-pointer" data-testid="terms-gate-terms-label">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              data-testid="terms-gate-terms-checkbox"
              className="mt-0.5 w-4 h-4 accent-spike-blue cursor-pointer flex-shrink-0"
            />
            <span className="text-sm text-spike-text leading-relaxed font-exo">
              I agree to the{" "}
              <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-spike-blue-light underline" data-testid="terms-gate-terms-link">
                Terms &amp; Conditions
              </Link>{" "}
              and{" "}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-spike-blue-light underline" data-testid="terms-gate-privacy-link">
                Privacy Policy
              </Link>
              . <span className="text-red-400">*</span>
            </span>
          </label>
          <label className="flex items-start gap-2.5 cursor-pointer" data-testid="terms-gate-marketing-label">
            <input
              type="checkbox"
              checked={marketing}
              onChange={(e) => setMarketing(e.target.checked)}
              data-testid="terms-gate-marketing-checkbox"
              className="mt-0.5 w-4 h-4 accent-spike-blue cursor-pointer flex-shrink-0"
            />
            <span className="text-sm text-spike-text-muted leading-relaxed font-exo">
              Send me drop-in deals, training tips and Spike Sports updates. Unsubscribe any time.
            </span>
          </label>

          {err ? (
            <div className="text-xs text-red-400 font-exo" data-testid="terms-gate-error">
              {err}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={busy || !accepted}
            data-testid="terms-gate-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 disabled:cursor-not-allowed text-black font-orbitron text-xs font-bold uppercase tracking-[0.2em] px-5 py-3 rounded-sm transition-colors"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Accept & Continue"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default TermsGateModal;
